/**
 * "Home" is per-session, not a constant.
 *
 * The sidebar/route table is server-driven and filtered per role
 * (`platform/tabs/tabs.services.ts`), so `/` is NOT guaranteed to exist: the
 * dashboard route is only registered when the user's role carries the
 * `dashboard` tab. Anything that hardcodes `/` as the landing spot — the
 * post-login redirect, the 404 page's "Go Back Home" — dead-ends a
 * dashboard-less role on a permanent 404. Always resolve through here.
 */
/**
 * Minimal structural shape of the `/tabs` payload this module reads. The real
 * payload carries more per-route metadata (key, label, icon, stageId, …) —
 * only `path` matters for choosing a landing spot.
 */
interface RouteLike {
  path?: string;
  key?: string;
}
interface GroupLike {
  group?: string;
  label?: string;
  routes?: RouteLike[];
}
export interface RouterTabsLike {
  dashboard?: RouteLike | null;
  whatsapp?: RouteLike | null;
  gmail?: RouteLike | null;
  groups?: (GroupLike | null)[];
}

export function resolveHomePath(
  tabs: RouterTabsLike | null | undefined,
): string | null {
  if (!tabs) return null;

  if (tabs.dashboard?.path) return normalize(tabs.dashboard.path);

  // Then the sidebar's own order: favourite views, stages, niche, management.
  // Whichever the user sees first is the least surprising place to land.
  for (const group of tabs.groups ?? []) {
    for (const route of group?.routes ?? []) {
      if (route?.path) return normalize(route.path);
    }
  }

  if (tabs.whatsapp?.path) return normalize(tabs.whatsapp.path);
  if (tabs.gmail?.path) return normalize(tabs.gmail.path);

  return null;
}

function normalize(path: string): string {
  return path.startsWith("/") ? path : `/${path}`;
}

/**
 * True for paths the SERVER mints, which embed the tenant id:
 *   stage tabs      → `/<stage name>-<tenantId>-<stageId>`
 *   favourite views → `/views/<slug>-<tenantId>-<viewId>`
 *
 * A user can only reach one of these from a link the app itself produced, so
 * an unmatched one is always a STALE link (back button, bookmark, a second tab,
 * or a session that switched tenant/user) — never a typo worth a 404 page.
 * Everything else (`/leads`, `/report`, a broken internal link) deliberately
 * fails to match, so genuine routing bugs still surface as a 404.
 */
export function isServerGeneratedDynamicPath(pathname: string): boolean {
  return /^\/(?:views\/)?[^/]+-\d+-\d+\/?$/.test(pathname);
}
